import type { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { BuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { CatalogItemBase } from "building-types/entities/catalogItemBase";
import type { OpeningHandle, RoomLayoutInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import type { InteriorMaterialOverride, ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { Awning } from "building-types/openings/awning";
import type { OpeningDescriptorBase } from "building-types/openings/openingDesciptorTypes";
import type { OpeningTagPlacement } from "building-types/openings/openingTagPlacementTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { Prop } from "building-types/props/propTypes";
import type { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { Room } from "building-types/rooms/roomTypes";
import type { AwningSettings } from "building-types/settings/awningSettings";
import type { DoorWallFramingSettings } from "building-types/settings/doorWallFramingSettings";
import type { DoorWallWrappingSettings } from "building-types/settings/doorWallWrappingSettings";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { InteriorDoorCasingSettings } from "building-types/settings/interiorDoorCasingSettings";
import type { DoorRoughOpeningSettings } from "building-types/settings/roughOpeningSettingsTypes";
import type { DoorViewNoteTable, AllDoorViewNoteTable } from "building-types/settings/viewNotesSettings";
import type GeomTagGroup from "geom/geomTagGroup";
import type { OBB2 } from "geom/obb2";
import type { LineAnchor, PolyLine2 } from "geom/polyLine2Types";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { BuiltinDoorId } from "./BuiltinDoorId";
import type { DoorOperationType } from "./doorOperationTypes";

interface DoorDescriptorBase extends OpeningDescriptorBase, CatalogItemBase<"DOOR_DESCRIPTOR"> {
  readonly id: BuiltinDoorId | string;
  readonly name?: string;
  readonly reportedName: string;
  readonly tagDetails?: string;
  readonly propIdPrefix?: DoorPropIdPrefix;
  readonly propId?: ForeignEntityId<"PROP">;
  readonly requiresDoorSwing: boolean;
  readonly swingSide?: SwingSide;
  readonly isExterior: boolean;
  readonly isPrimaryDoor: boolean;
  readonly doorLeafCount: number;
  readonly includeTrim: boolean;
  readonly operationType: DoorOperationType;
  readonly segmentedArc?: boolean;
  readonly arcFactor?: number;
  readonly arcDepth: number;
  readonly roughOpeningAdjustment?: number; // TODO: remove?
  readonly zOffset: number;
  readonly requiresHeightComputation: boolean;
  readonly panelThickness?: number;
  readonly panelOffsetDepth?: number;
  readonly exteriorCasing?: ExteriorDoorCasingSettings;
  readonly interiorCasing?: InteriorDoorCasingSettings;
  readonly viewNotes?: DoorViewNoteTable;
  readonly staticHeight?: number;
  readonly includeDriveway?: boolean;
  readonly roughOpeningSettings?: DoorRoughOpeningSettings;
  /**
   * This is not pretty, but is used in this case to prevent
   * regression of legacy garage door types that previously
   * were not correctly recognized as garage doors.
   */
  readonly isLegacyGarageDoorType?: boolean;

  readonly framing?: DoorWallFramingSettings;
  readonly wrapping?: DoorWallWrappingSettings;
}

/**
 * This is the runtime type for a door descriptor, which can be a builtin door or a custom door.
 */
export interface DoorDescriptor extends DoorDescriptorBase {
  readonly id: BuiltinDoorId | string;
}

/**
 * This is the type that is stored in the catalog in the Doc and can be rekeyed. It does not include
 * builtin doors.
 */
export interface DoorDescriptorCustom extends DoorDescriptorBase {
  readonly id: PrimaryEntityId<"DOOR_DESCRIPTOR">;
}

interface GeneratedDoorPlacementBase extends OpeningTagPlacement {
  readonly id: string;
  readonly entityInstanceType: EntityInstanceType;
  readonly edgeId: string; // room edge id
  readonly descriptor: DoorDescriptor;
  readonly frontRoom: Room;
  readonly backRoom: Room;
  readonly halfEdgeIndex: number;
  readonly swingSide: SwingSide | SwingSideGenerator;
  readonly awning?: Awning;
  readonly awningSettings?: Partial<AwningSettings>;
  readonly exteriorCasing?: Partial<ExteriorDoorCasingSettings>;
  readonly interiorCasing?: Partial<InteriorDoorCasingSettings>;
  readonly includeDriveway?: boolean;
  readonly framing?: Partial<DoorWallFramingSettings>;
  // @todo(Andrew): split this into front and back wrapping settings
  readonly wrapping?: Partial<DoorWallWrappingSettings>;
  readonly viewNotes?: DoorViewNoteTable;
  readonly disableAutoDimensions?: boolean;
  readonly param: number;
  readonly paramNormalized: boolean;
  readonly paramAnchor: LineAnchor;
  readonly verticalOffset: number;
  readonly lateralOffset: number;
  readonly handle?: OpeningHandle | RoomLayoutInstanceHandle;
  readonly handleAuxiliary?: OpeningHandle; // When the handle is RoomLayoutInstanceHandle, this is the handle of the original entity.
  readonly ignoreOppositeWallCollision?: boolean;

  readonly reportedName?: string;

  /* doors that cannot be placed (eg because they dont fit in the host wall) have no opening */
  opening?: WallOpening;
  /* doors without SwingSideGenerator algorithms set as the default have the swing side stored here */
  computedSwingSide?: SwingSide;
  /* only valid for arched doors */
  springHeight?: number;
  streetOrientation: StreetOrientation;

  /**
   * is there *any* casing present on this door
   */
  readonly hasExteriorCasing: boolean;
  readonly hasInteriorCasing: boolean;
  /**
   * if *any* casing clips finish, this is true
   */
  readonly casingClipsFinish: boolean;
}

/**
 * Identifies the position of the door, but not its geometry. DoorUnit stores the actual
 * geometrical content of the door.
 */
export type GeneratedDoorPlacement = GeneratedDoorPlacementDeprecated | GeneratedDoorPlacementV2;

export interface GeneratedDoorPlacementDeprecated extends GeneratedDoorPlacementBase {
  readonly type: "DOOR";
  readonly wallStripDomain?: boolean;
  readonly useStripLimits?: boolean;
  /* wallStripDomain doors store a reversed value here instead of reversing the front/back room */
  readonly reverse?: boolean;
}

export interface GeneratedDoorPlacementV2 extends GeneratedDoorPlacementBase {
  readonly type: "DOOR_V2";
  readonly reverse: boolean;
  readonly x: number; // doors get projected to wall face edges and this x coordinate converts the room edge position to the wall face edge position
  readonly y: number; // doors get projected to wall face edges and this y coordinate converts the room edge position to the wall face edge position
  readonly halfEdgeParam: number; // the door's position along its half edge
}

export interface DoorOperationInfo {
  readonly planOutlineInteriorLinework: PolyLine2[]; // dashed
  readonly planOutlineExteriorLinework: PolyLine2[]; // solid
  readonly sliderOperationArrowLinework: PolyLine2[]; // solid
  readonly doorSwingLinework: PolyLine2[]; // solid
  readonly doorBodyLinework: PolyLine2[]; // solid
  readonly doorBodyFixedLinework: PolyLine2[]; //solid
  readonly overheadOperationLinework: PolyLine2[]; // dashed
  readonly operationLinework: PolyLine2[]; // dashed
  readonly doorBodyOBBs: OBB2[];
  readonly doorSwingLineworkOBBs: OBB2[];

  /**
   * Front refers to the room the door was placed from. The Back is the opposite room side.
   */
  readonly frontRightSwing?: number;
  readonly frontLeftSwing?: number;
  readonly backRightSwing?: number;
  readonly backLeftSwing?: number;
  readonly doorSwingStopsAtCorners: boolean; // PocketDoors for example don't need to prevent electrical placement around corners from the opening

  readonly frontRightIsLatchSide: boolean;
  readonly frontLeftIsLatchSide: boolean;
}

export interface DoorUnit extends BuildingEntityBase<"DOOR"> {
  readonly doorPlacement: GeneratedDoorPlacement;
  readonly interiorFront: GeomTagGroup<InteriorMaterialOverride>;
  readonly interiorBack: GeomTagGroup<InteriorMaterialOverride>;
  readonly exteriorFront: GeomTagGroup<ExteriorMaterialOverride>;
  readonly exteriorBack: GeomTagGroup<ExteriorMaterialOverride>;
  readonly streetOrientation: StreetOrientation;
  readonly prop: Prop | undefined;
  readonly swingSide: SwingSide;
  readonly decorations: GeomTagGroup<ExteriorMaterialOverride>;
  readonly awningRoof: GeomTagGroup<ExteriorMaterialOverride>;
  readonly props: Prop[];
  // @TODO(Andrew): use merged settings here, re-write queries to use the DoorUnit rather than Door
  readonly exteriorCasingSettings: ExteriorDoorCasingSettings | undefined;
  readonly interiorCasingSettings: InteriorDoorCasingSettings | undefined;
  readonly viewNotes?: AllDoorViewNoteTable;
  readonly headCasingHeight: number;
  readonly arcCurve?: PolyLine2;
}

export enum SwingSideGenerator {
  AGAINST_SOLID_WALL = "AGAINST_SOLID_WALL",
}

export enum SwingSide {
  NONE = "NONE",
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

export enum DoorHanding {
  NONE = "NONE",
  LEFT_HAND = "LEFT_HAND",
  RIGHT_HAND = "RIGHT_HAND",
  LEFT_HAND_REVERSE = "LEFT_HAND_REVERSE",
  RIGHT_HAND_REVERSE = "RIGHT_HAND_REVERSE",
}

export enum DoorPropIdPrefix {
  DoorInterior = "doorInterior",
  DoorInteriorCloset = "doorInteriorCloset",
  DoorInteriorLouvred = "doorInteriorLouvred",
  DoorInteriorPocket = "doorInteriorPocket",
  DoorInteriorBiFold = "doorInteriorBiFold",
  DoorInteriorDouble = "doorInteriorDouble",
  DoorInteriorDoubleCloset = "doorInteriorDoubleCloset",
  DoorDoubleFrench = "doorDoubleFrench",
  DoorDoubleFrench15Lite = "doorDoubleFrench15Lite",
  DoorInteriorDoubleBypass = "doorInteriorDoubleBypass",
  DoorInteriorBarn = "doorInteriorBarn",
  DoorSlidingGlass = "doorSlidingGlass",
  DoorGarage = "doorGarage",
}
